const SIDEBAR_ICON_COLOURS = ["#e4a4a5", "#234d6f", "#597058", "#ff8600", "#c03831", "#3397da"];

type Rgb = { r: number; g: number; b: number };
type Hsl = { h: number; s: number; l: number };

function hashSeed(seed: string) {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function randomFromSeed(seed?: string) {
  if (!seed) return Math.random();
  let value = hashSeed(seed);
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  return ((value >>> 0) % 100000) / 100000;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function normalizeHexColour(value?: string | null) {
  const trimmed = String(value || "").trim();
  const match = trimmed.match(/^#?([0-9a-f]{6})$/i);
  return match ? `#${match[1].toLowerCase()}` : null;
}

export function hexToRgb(hex: string): Rgb {
  const normalized = normalizeHexColour(hex);
  if (!normalized) throw new Error(`Invalid hex colour: ${hex}`);
  const value = normalized.slice(1);
  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }: Rgb) {
  return `#${[r, g, b].map((channel) => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, "0")).join("")}`;
}

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let h = 0;

  if (delta !== 0) {
    if (max === red) h = ((green - blue) / delta) % 6;
    else if (max === green) h = (blue - red) / delta + 2;
    else h = (red - green) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  return { h, s: s * 100, l: l * 100 };
}

function hslToRgb({ h, s, l }: Hsl): Rgb {
  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lightness - chroma / 2;
  let red = 0;
  let green = 0;
  let blue = 0;

  if (h < 60) [red, green, blue] = [chroma, x, 0];
  else if (h < 120) [red, green, blue] = [x, chroma, 0];
  else if (h < 180) [red, green, blue] = [0, chroma, x];
  else if (h < 240) [red, green, blue] = [0, x, chroma];
  else if (h < 300) [red, green, blue] = [x, 0, chroma];
  else [red, green, blue] = [chroma, 0, x];

  return {
    r: (red + m) * 255,
    g: (green + m) * 255,
    b: (blue + m) * 255,
  };
}

export function getRelativeLuminance(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  const channels = [r, g, b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

export function getContrastRatio(leftHex: string, rightHex: string) {
  const left = getRelativeLuminance(leftHex);
  const right = getRelativeLuminance(rightHex);
  const lighter = Math.max(left, right);
  const darker = Math.min(left, right);
  return (lighter + 0.05) / (darker + 0.05);
}

export function getReadableTextColour(backgroundHex: string) {
  return getContrastRatio(backgroundHex, "#0f172a") >= getContrastRatio(backgroundHex, "#ffffff")
    ? "#0f172a"
    : "#ffffff";
}

export function generateProjectShowColour(seed?: string) {
  const resolvedSeed = seed || `${Date.now()}-${Math.random()}`;
  const first = randomFromSeed(`${resolvedSeed}:base`);
  const second = randomFromSeed(`${resolvedSeed}:hue`);
  const third = randomFromSeed(`${resolvedSeed}:sat`);
  const fourth = randomFromSeed(`${resolvedSeed}:light`);
  const base = SIDEBAR_ICON_COLOURS[Math.floor(first * SIDEBAR_ICON_COLOURS.length) % SIDEBAR_ICON_COLOURS.length];
  const hsl = rgbToHsl(hexToRgb(base));

  const nextHsl: Hsl = {
    h: (hsl.h + (second - 0.5) * 34 + 360) % 360,
    s: clamp(hsl.s + (third - 0.5) * 18, 44, 72),
    l: clamp(hsl.l + (fourth - 0.5) * 10, 38, 60),
  };

  let adjustedHsl = nextHsl;
  let colour = rgbToHex(hslToRgb(adjustedHsl));

  for (let attempt = 0; attempt < 12 && getContrastRatio(colour, getReadableTextColour(colour)) < 4.5; attempt += 1) {
    const luminance = getRelativeLuminance(colour);
    adjustedHsl = {
      ...adjustedHsl,
      l: luminance < 0.23 ? clamp(adjustedHsl.l - 2, 32, 60) : clamp(adjustedHsl.l + 2, 32, 64),
    };
    colour = rgbToHex(hslToRgb(adjustedHsl));
  }

  return colour;
}

export function isProjectShowColourSafe(hex: string) {
  const normalized = normalizeHexColour(hex);
  if (!normalized) return false;
  const luminance = getRelativeLuminance(normalized);
  const text = getReadableTextColour(normalized);
  return luminance >= 0.06 && luminance <= 0.52 && getContrastRatio(normalized, text) >= 4.5;
}
